export interface CelestialRecord {
  id: string;
  playerName: string;
  timeSeconds: number; // Elapsed time in seconds with decimal (e.g. 134.5)
  formattedTime: string; // "02:14.5"
  timestamp: number;
  dateStr: string;
  completed: boolean;
  starsReached: number; // out of 50
  livesLeft: number;
  maxCombo: number;
}

const STORAGE_KEY = 'kabyleGame_celestial_leaderboard_v2';
const STORAGE_KEY_HISTORY = 'kabyleGame_celestial_player_history_v2';

// Format seconds into MM:SS.d
export function formatCelestialTime(seconds: number, showDecimals: boolean = true): string {
  if (isNaN(seconds) || seconds < 0) seconds = 0;
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  const tenths = Math.floor((seconds * 10) % 10);

  const minsStr = mins.toString().padStart(2, '0');
  const secsStr = secs.toString().padStart(2, '0');

  if (showDecimals) {
    return `${minsStr}:${secsStr}.${tenths}`;
  }
  return `${minsStr}:${secsStr}`;
}

// Initial legendary champion records to make the celestial leaderboard engaging
const SEED_LEGENDARY_RECORDS: CelestialRecord[] = [
  {
    id: 'legend_igider',
    playerName: 'Igider (Aigle)',
    timeSeconds: 128.4,
    formattedTime: '02:08.4',
    timestamp: Date.now() - 86400000 * 3,
    dateStr: 'Légende Céleste',
    completed: true,
    starsReached: 50,
    livesLeft: 5,
    maxCombo: 50
  },
  {
    id: 'legend_uccen',
    playerName: 'Uccen (Chacal)',
    timeSeconds: 154.2,
    formattedTime: '02:34.2',
    timestamp: Date.now() - 86400000 * 2,
    dateStr: 'Légende Céleste',
    completed: true,
    starsReached: 50,
    livesLeft: 4,
    maxCombo: 38
  },
  {
    id: 'legend_amekasa',
    playerName: 'Ameksa (Berger)',
    timeSeconds: 196.8,
    formattedTime: '03:16.8',
    timestamp: Date.now() - 86400000 * 1,
    dateStr: 'Légende Céleste',
    completed: true,
    starsReached: 50,
    livesLeft: 3,
    maxCombo: 24
  }
];

export function getCelestialLeaderboard(): CelestialRecord[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      // Return seeded records
      localStorage.setItem(STORAGE_KEY, JSON.stringify(SEED_LEGENDARY_RECORDS));
      return SEED_LEGENDARY_RECORDS;
    }
    const parsed: CelestialRecord[] = JSON.parse(raw);
    if (!Array.isArray(parsed) || parsed.length === 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(SEED_LEGENDARY_RECORDS));
      return SEED_LEGENDARY_RECORDS;
    }
    // Sort by fastest time first
    return parsed.sort((a, b) => a.timeSeconds - b.timeSeconds);
  } catch (e) {
    console.error('Error reading celestial leaderboard', e);
    return SEED_LEGENDARY_RECORDS;
  }
}

export function getPlayerCelestialHistory(playerName: string): CelestialRecord[] {
  try {
    const raw = localStorage.getItem(`${STORAGE_KEY_HISTORY}_${playerName.toLowerCase()}`);
    if (!raw) return [];
    const list: CelestialRecord[] = JSON.parse(raw);
    return Array.isArray(list) ? list.sort((a, b) => b.timestamp - a.timestamp) : [];
  } catch (e) {
    console.error('Error reading player celestial history', e);
    return [];
  }
}

export function getPlayerBestTime(playerName: string): CelestialRecord | null {
  const history = getPlayerCelestialHistory(playerName).filter(r => r.completed);
  if (history.length === 0) {
    // Check general leaderboard
    const all = getCelestialLeaderboard().filter(r => r.playerName.toLowerCase() === playerName.toLowerCase() && r.completed);
    if (all.length === 0) return null;
    return all.sort((a, b) => a.timeSeconds - b.timeSeconds)[0];
  }
  return history.sort((a, b) => a.timeSeconds - b.timeSeconds)[0];
}

export function getPlayerPreviousAttempt(playerName: string): CelestialRecord | null {
  const history = getPlayerCelestialHistory(playerName);
  if (history.length === 0) return null;
  // Return the most recent attempt before the current run (index 0 if viewing existing history)
  return history[0] || null;
}

export interface SaveRecordResult {
  record: CelestialRecord;
  isNewPersonalBest: boolean;
  previousBestTime: number | null;
  previousAttemptTime: number | null;
  diffWithPreviousAttempt: number | null; // negative = faster / better!
  diffWithPersonalBest: number | null; // negative = faster / better!
  rank: number;
}

export function saveCelestialRunRecord(
  playerName: string,
  timeSeconds: number,
  completed: boolean,
  starsReached: number,
  livesLeft: number,
  maxCombo: number
): SaveRecordResult {
  const cleanName = playerName?.trim() || 'Voyageur';
  const now = Date.now();
  const dateStr = new Date(now).toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });

  const formattedTime = formatCelestialTime(timeSeconds, true);

  // Retrieve prior personal best and prior attempt before saving new record
  const prevPB = getPlayerBestTime(cleanName);
  const prevAttempt = getPlayerPreviousAttempt(cleanName);

  const previousBestTime = prevPB ? prevPB.timeSeconds : null;
  const previousAttemptTime = prevAttempt ? prevAttempt.timeSeconds : null;

  const newRecord: CelestialRecord = {
    id: `run_${now}_${Math.random().toString(36).substr(2, 6)}`,
    playerName: cleanName,
    timeSeconds: Math.round(timeSeconds * 10) / 10,
    formattedTime,
    timestamp: now,
    dateStr,
    completed,
    starsReached,
    livesLeft,
    maxCombo
  };

  // Save to player's personal history
  try {
    const existingHistory = getPlayerCelestialHistory(cleanName);
    const updatedHistory = [newRecord, ...existingHistory].slice(0, 50); // Keep last 50 runs
    localStorage.setItem(
      `${STORAGE_KEY_HISTORY}_${cleanName.toLowerCase()}`,
      JSON.stringify(updatedHistory)
    );
  } catch (e) {
    console.error('Error saving player celestial history', e);
  }

  // Save to all-time leaderboard if completed
  let rank = 1;
  let isNewPersonalBest = false;

  if (completed) {
    if (!previousBestTime || timeSeconds < previousBestTime) {
      isNewPersonalBest = true;
    }

    try {
      const allLeaderboard = getCelestialLeaderboard();
      const updatedLeaderboard = [...allLeaderboard, newRecord]
        .sort((a, b) => a.timeSeconds - b.timeSeconds)
        .slice(0, 100); // Keep top 100

      localStorage.setItem(STORAGE_KEY, JSON.stringify(updatedLeaderboard));

      rank = updatedLeaderboard.findIndex(r => r.id === newRecord.id) + 1;
      if (rank <= 0) rank = updatedLeaderboard.length;
    } catch (e) {
      console.error('Error saving leaderboard', e);
    }
  }

  const diffWithPreviousAttempt = previousAttemptTime !== null ? timeSeconds - previousAttemptTime : null;
  const diffWithPersonalBest = previousBestTime !== null ? timeSeconds - previousBestTime : null;

  return {
    record: newRecord,
    isNewPersonalBest,
    previousBestTime,
    previousAttemptTime,
    diffWithPreviousAttempt,
    diffWithPersonalBest,
    rank
  };
}
